import { LoadingButton } from "@mui/lab";
import { Box, Chip, DialogTitle, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useTranslation } from "react-i18next";
import { forwardRef, useImperativeHandle, useState } from "react";

import { BccRecipientListModel } from "../types/BccRecipientListTypes";

/**
 * Handle type for DialogBccRecipientList
 * Use this with useRef to control the dialog imperatively
 */
export type DialogBccRecipientListHandle = {
	show: (data: BccRecipientListModel) => void;
};

export type DialogBccRecipientListProps = Readonly<{
	/** Callback when save is clicked, receives the updated model */
	onSave: (model: BccRecipientListModel) => Promise<boolean>;
	/** Whether a save operation is in progress */
	isSaving?: boolean;
}>;

/**
 * Dialog for editing BCC recipients for a specific email type
 * Uses forwardRef pattern with imperative show() method
 */
export const DialogBccRecipientList = forwardRef<
	DialogBccRecipientListHandle,
	DialogBccRecipientListProps
>((props, ref) => {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [model, setModel] = useState<BccRecipientListModel | null>(null);
	const [recipients, setRecipients] = useState<string[]>([]);
	const [newEmail, setNewEmail] = useState<string>("");
	const [emailError, setEmailError] = useState<string | null>(null);

	// Expose show() method via ref
	useImperativeHandle(ref, () => ({
		show: (data: BccRecipientListModel) => {
			setModel(data);
			setRecipients([...data.recipients]);
			setNewEmail("");
			setEmailError(null);
			setOpen(true);
		},
	}));

	const handleClose = () => {
		setOpen(false);
		setModel(null);
		setRecipients([]);
		setNewEmail("");
		setEmailError(null);
	};

	const isValidEmail = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleAddEmail = () => {
		const trimmedEmail = newEmail.trim();
		if (!trimmedEmail) {
			return;
		}
		if (!isValidEmail(trimmedEmail)) {
			setEmailError(t("invalid-email", { ns: "validation" }));
			return;
		}
		if (recipients.includes(trimmedEmail)) {
			setEmailError(t("email-already-added", { ns: "validation" }));
			return;
		}
		setRecipients([...recipients, trimmedEmail]);
		setNewEmail("");
		setEmailError(null);
	};

	const handleRemoveEmail = (emailToRemove: string) => {
		setRecipients(recipients.filter((email) => email !== emailToRemove));
	};

	const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			handleAddEmail();
		}
	};

	const handleSubmit = async () => {
		if (!model) return;

		const updatedModel: BccRecipientListModel = {
			...model,
			recipients,
		};

		const success = await props.onSave(updatedModel);
		if (success) {
			handleClose();
		}
	};

	const getTypeLabel = () => {
		if (!model) return "";
		const type = model.type.toLowerCase();
		return t(`emailtype.${type}`, { ns: "enum" });
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			data-testid="bcc-recipient-list-dialog"
			aria-describedby="alert-dialog-description"
			maxWidth="sm"
			fullWidth={true}
		>
			<DialogTitle>
				{t("bcc-recipients")}: {getTypeLabel()}
			</DialogTitle>
			<DialogContent className="flex-col gap-4">
				<Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 2 }}>
					<TextField
						data-testid="bcc-recipient-email-input"
						onChange={(event) => {
							setNewEmail(event.target.value);
							setEmailError(null);
						}}
						onKeyDown={handleKeyPress}
						error={!!emailError}
						helperText={emailError ?? ""}
						sx={{ flex: 1 }}
						value={newEmail}
						label={t("email-address")}
						variant="outlined"
						placeholder={t("enter-email-address")}
					/>
					<Button
						data-testid="bcc-recipient-add-button"
						variant="contained"
						onClick={handleAddEmail}
						sx={{ mt: 1 }}
					>
						{t("add")}
					</Button>
				</Box>
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
					{recipients.map((email) => (
						<Chip
							key={email}
							label={email}
							onDelete={() => handleRemoveEmail(email)}
							data-testid={`bcc-recipient-chip-${email}`}
						/>
					))}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button data-testid="bcc-recipient-cancel-button" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					data-testid="bcc-recipient-save-button"
					loading={props.isSaving}
					onClick={handleSubmit}
					className={props.isSaving ? "" : "blueButton"}
				>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
});

DialogBccRecipientList.displayName = "DialogBccRecipientList";
